/* eslint-disable react/sort-comp */
import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { EditorState, RichUtils, AtomicBlockUtils } from 'draft-js';
import { getSelectionEntity, setBlockData } from 'draftjs-utils';
import Editor, { composeDecorators } from 'draft-js-plugins-editor';
import createImagePlugin from 'draft-js-image-plugin';
import createFocusPlugin from 'draft-js-focus-plugin';
import 'draft-js/dist/Draft.css';
import 'draft-js-image-plugin/lib/plugin.css';
import 'draft-js-focus-plugin/lib/plugin.css';
import { FormattedMessage } from 'react-intl';
import {
  setEditorState,
  getCurrentTextAlignment,
  isCurrentlySelectingText,
  isCurrentlyCursorOnLink,
  isCurrentlySelectingImage,
  getEntity,
  getBlockStyleFn,
} from '../../../utils/html-utils';
import { myInputHTMLIcons } from '../../../icon/seed-icons';
import MyInputHTMLImage from './MyInputHTMLImage';
import MyInputHTMLImageDialog from './MyInputHTMLImageDialog';
import MyInputHTMLLinkDialog from './MyInputHTMLLinkDialog';
import MyInputHTMLToolbar from './MyInputHTMLToolbar';
import pageUtilsPropTypes from '../../../page/page-prop-types';


const focusPlugin = createFocusPlugin();
const decorator = composeDecorators(
  focusPlugin.decorator,
);
const imagePlugin = createImagePlugin({
  imageComponent: MyInputHTMLImage,
  decorator,
});
const plugins = [imagePlugin, focusPlugin];

const styles = (theme) => ({
  editor: {
    '& .DraftEditor-root': {
      marginTop: 12,
      border: (props) => (props.error ? `2px solid ${theme.palette.error.main}` : '2px solid #ced4da'),
      borderRadius: 4,
      transition: 'border 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
      '&:focus-within': {
        border: `2px solid ${theme.palette.primary.main}`,
      },
      color: theme.palette.text.primary,
    },
    '& .DraftEditor-editorContainer>div>div': {
      padding: '10px 12px',
      minHeight: 150,
      maxHeight: 300,
      overflowY: 'auto',
    },
    '& .public-DraftStyleDefault-block': {
      marginBottom: 8,
    },
    '& .align-right div': {
      textAlign: 'right',
    },
    '& .align-center div': {
      textAlign: 'center',
    },
    '& .align-left div': {
      // textAlign: "left",
    },
    '& .align-justify div': {
      textAlign: 'justify',
    },
    '& a': {
      color: theme.palette.secondary.main,
    },
  },
});

const inlineStyles = [
  {
    name: 'BOLD',
    label: 'Bold',
    icon: <myInputHTMLIcons.FormatBold />,
  },
  {
    name: 'ITALIC',
    label: 'Italic',
    icon: <myInputHTMLIcons.FormatItalic />,
  },
  {
    name: 'UNDERLINE',
    label: 'Underline',
    icon: <myInputHTMLIcons.FormatUnderlined />,
  },
];

const blockStyles = [
  {
    name: 'unordered-list-item',
    label: 'Bullets',
    icon: <myInputHTMLIcons.FormatListBulleted />,
  },
  {
    name: 'ordered-list-item',
    label: 'Numbering',
    icon: <myInputHTMLIcons.FormatListNumbered />,
  },
];

const alignmentStyles = [
  {
    name: 'align-left',
    label: 'Align Left',
    icon: <myInputHTMLIcons.FormatAlignLeft />,
  },
  {
    name: 'align-center',
    label: 'Align Center',
    icon: <myInputHTMLIcons.FormatAlignCenter />,
  },
  {
    name: 'align-right',
    label: 'Align Right',
    icon: <myInputHTMLIcons.FormatAlignRight />,
  },
  {
    name: 'align-justify',
    label: 'Align Justify',
    icon: <myInputHTMLIcons.FormatAlignJustify />,
  },
];

class MyInputHTML extends React.Component {
  constructor(props) {
    super(props);
    const {
      editorState,
      name,
      pageUtils,
      repository,
      onChange,
    } = props;
    this.state = {
      imageDialogOpen: false,
      url: '',
      caption: '',
      link: '',
      urlDialogOpen: false,
      editorState,
    };
    this.pageUtils = pageUtils;
    this.repo = repository;

    this.onChange = (newEditorState) => {
      this.setState({
        editorState: newEditorState,
      });
      if (onChange) {
        onChange({
          skipPersist: true,
          target: {
            name,
            value: newEditorState,
          },
        });
      }
    };
    this.handleKeyCommand = this.handleKeyCommand.bind(this);
    this.handleChangeHeading = this.handleChangeHeading.bind(this);
    this.handleToggleInlineStyle = this.handleToggleInlineStyle.bind(this);
    this.handleToggleBlockStyle = this.handleToggleBlockStyle.bind(this);
    this.handleClickUndo = this.handleClickUndo.bind(this);
    this.handleClickRedo = this.handleClickRedo.bind(this);
  }

  handleKeyCommand(command, editorState) {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      this.onChange(newState);
      return 'handled';
    }
    return 'not-handled';
  }

  handleChangeHeading(event) {
    if (event.target.value === 'atomic') {
      this.handleClickAddImage();
      return;
    }
    this.handleToggleBlockStyle(event.target.value);
  }

  handleToggleInlineStyle(style) {
    const { editorState } = this.state;
    this.onChange(RichUtils.toggleInlineStyle(editorState, style));
  }

  handleToggleBlockStyle(style) {
    const { editorState } = this.state;
    this.onChange(RichUtils.toggleBlockType(editorState, style));
  }

  handleClickUndo() {
    const { editorState } = this.state;
    this.onChange(EditorState.undo(editorState));
  }

  handleClickRedo() {
    const { editorState } = this.state;
    this.onChange(EditorState.redo(editorState));
  }

  /* Image Dialog */

  handleClickAddImage = () => {
    this.setState({
      url: '',
      caption: '',
      imageDialogOpen: true,
    });
  };

  handleClickCloseImageDialog = () => {
    this.setState({ imageDialogOpen: false });
  }

  handleClickEditImage = () => {
    const {
      editorState,
    } = this.state;
    const entityKey = getSelectionEntity(editorState);
    const image = getEntity(editorState, 'IMAGE', entityKey);
    this.setState({
      url: image.src,
      caption: image.subtitle,
      imageDialogOpen: true,
    });
  }

  handleConfirmAddImage = (values) => {
    this.setState({ caption: values.myInputHTML_caption });
    if (values.myInputHTML_image instanceof File) {
      this.handleChangeUploadFile(values.myInputHTML_image, true);
    } else {
      this.addNewImage();
    }
  }

  addNewImage() {
    const {
      url,
      caption,
      editorState,
    } = this.state;
    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity(
      'IMAGE',
      'MUTABLE',
      {
        src: url,
        subtitle: caption,
      },
    );
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const newEditorState = setEditorState(
      editorState,
      contentStateWithEntity,
    );
    this.onChange(AtomicBlockUtils.insertAtomicBlock(newEditorState, entityKey, entityKey));
    this.handleClickCloseImageDialog();
  }

  handleConfirmEditImage = (values) => {
    this.setState({ caption: values.myInputHTML_caption });
    if (values.myInputHTML_image instanceof File) {
      this.handleChangeUploadFile(values.myInputHTML_image, false);
    } else {
      this.editImage();
    }
  }

  editImage() {
    const {
      url,
      caption,
      editorState,
    } = this.state;
    const entityKey = getSelectionEntity(editorState);
    const contentStateWithEntity = editorState.getCurrentContent()
      .replaceEntityData(entityKey, {
        src: url,
        subtitle: caption,
      });
    const newEditorState = setEditorState(
      editorState,
      contentStateWithEntity,
    );
    this.onChange(newEditorState);
    this.handleClickCloseImageDialog();
  }

  /* EOF Image Dialog */

  /* URL Dialog */

  handleClickAddUrl = () => {
    this.setState({
      link: '',
      urlDialogOpen: true,
    });
  }

  handleClickCloseUrlDialog = () => {
    this.setState({ urlDialogOpen: false });
  }

  handleClickEditUrl = () => {
    const {
      editorState,
    } = this.state;
    const entityKey = getSelectionEntity(editorState);
    const entity = getEntity(editorState, 'LINK', entityKey);
    this.setState({
      link: entity.url,
      urlDialogOpen: true,
    });
  }

  handleConfirmAddUrl = (values) => {
    this.setState({ link: values.myInputHTML_link });
    const {
      editorState,
    } = this.state;

    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity(
      'LINK',
      'MUTABLE',
      {
        url: values.myInputHTML_link,
      },
    );
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const newEditorState = setEditorState(editorState, contentStateWithEntity);
    this.onChange(RichUtils.toggleLink(
      newEditorState,
      newEditorState.getSelection(),
      entityKey,
    ));
    this.handleClickCloseUrlDialog();
  }

  handleConfirmEditUrl = (values) => {
    this.setState({ link: values.myInputHTML_link });
    const {
      editorState,
    } = this.state;

    const entityKey = getSelectionEntity(editorState);
    const contentStateWithEntity = editorState.getCurrentContent().replaceEntityData(entityKey, {
      url: values.myInputHTML_link,
    });
    const newEditorState = setEditorState(
      editorState,
      contentStateWithEntity,
    );
    this.onChange(newEditorState);
    this.handleClickCloseUrlDialog();
  }

  /* EOF URL Dialog */

  handleChangeUploadFile = (file, isAdd) => {
    this.pageUtils.loadSomething(
      () => this.repo.uploadFile(file),
      (url) => {
        this.setState({ url });
        if (isAdd) {
          this.addNewImage();
        } else {
          this.editImage();
        }
      },
      '',
      <FormattedMessage id="myInputHTML.form.image.error" />,
      false,
    );
  }

  setTextAlignment = (align) => {
    const { editorState } = this.state;
    const newEditorState = setBlockData(editorState, {
      align,
    });
    this.onChange(newEditorState);
  }

  render() {
    const {
      classes,
      disabled,
      inputRef,
      enableImage,
    } = this.props;

    const {
      url,
      caption,
      imageDialogOpen,
      link,
      urlDialogOpen,
      editorState,
    } = this.state;

    const isSelectLink = isCurrentlyCursorOnLink(editorState);
    const isImageSelected = isCurrentlySelectingImage(editorState);
    const isSelect = isCurrentlySelectingText(editorState);
    const align = getCurrentTextAlignment(editorState);

    return (
      <>
        <MyInputHTMLToolbar
          editorState={editorState}
          onChangeHeading={this.handleChangeHeading}
          inlineStyles={inlineStyles}
          onToggleInlineStyle={this.handleToggleInlineStyle}
          blockStyles={blockStyles}
          onToggleBlockStyle={this.handleToggleBlockStyle}
          icons={myInputHTMLIcons}
          onClickUndo={this.handleClickUndo}
          onClickRedo={this.handleClickRedo}

          onClickAddLink={this.handleClickAddUrl}
          onClickEditLink={this.handleClickEditUrl}
          isCursorOnLink={isSelectLink}
          isSelectingText={isSelect}

          enableImage={enableImage}
          onClickAddImage={this.handleClickAddImage}
          onClickEditImage={this.handleClickEditImage}
          isSelectingImage={isImageSelected}

          currentAlignment={align}
          alignmentStyles={alignmentStyles}
          onChangeAlignment={this.setTextAlignment}

          disabled={disabled}
        />
        <div
          className={classes.editor}
        >
          <Editor
            blockStyleFn={getBlockStyleFn}
            readOnly={disabled}
            editorState={editorState}
            handleKeyCommand={this.handleKeyCommand}
            onChange={this.onChange}
            plugins={plugins}
            ref={inputRef}
          />
        </div>

        <MyInputHTMLImageDialog
          disableBackdropClick={url !== ''}
          open={imageDialogOpen}
          onClose={this.handleClickCloseImageDialog}
          onConfirm={isImageSelected ? this.handleConfirmEditImage : this.handleConfirmAddImage}
          disabled={disabled}
          addImage={!isImageSelected}
          initialValues={{
            myInputHTML_image: url,
            myInputHTML_caption: caption,
          }}
        />

        <MyInputHTMLLinkDialog
          open={urlDialogOpen}
          onClose={this.handleClickCloseUrlDialog}
          onConfirm={isSelectLink ? this.handleConfirmEditUrl : this.handleConfirmAddUrl}
          disabled={disabled}
          addImage={!isSelectLink}
          initialValues={{
            myInputHTML_link: link,
          }}
        />
      </>
    );
  }
}

MyInputHTML.propTypes = {
  name: PropTypes.string.isRequired,
  pageUtils: pageUtilsPropTypes.isRequired,
  repository: PropTypes.any.isRequired,
  classes: PropTypes.shape({
    editor: PropTypes.string,
  }).isRequired,
  editorState: PropTypes.object.isRequired,

  enableImage: PropTypes.bool,
  inputRef: PropTypes.any,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  // eslint-disable-next-line react/no-unused-prop-types
  error: PropTypes.bool,
};

MyInputHTML.defaultProps = {
  inputRef: undefined,
  onChange: undefined,
  disabled: false,
  enableImage: true,
  error: false,
};

export default withStyles(styles, { withTheme: true })(MyInputHTML);
